import { useEffect, useRef } from 'react';
import { useAuth } from '../store/auth';

/** Decode JWT exp claim without verifying signature. */
function jwtExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp && Date.now() / 1000 > payload.exp;
  } catch {
    return false;
  }
}

/**
 * Connect to the admin WebSocket and call onEvent for each live event.
 * Auto-reconnects on disconnect with exponential backoff.
 * Stops retrying if the token is expired (avoids hammering server with 403s).
 * Messages sent before the WS is open are buffered and flushed on open.
 */
export function useLiveEvents(onEvent) {
  const { token, logout } = useAuth();
  const wsRef      = useRef(null);
  const timerRef   = useRef(null);
  const cbRef      = useRef(onEvent);
  const bufRef     = useRef([]);   // messages buffered while WS is connecting
  const backoffRef = useRef(3000); // start at 3s, cap at 30s
  cbRef.current = onEvent;

  const send = (msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } else {
      bufRef.current.push(msg);
    }
  };

  useEffect(() => {
    if (!token) return;

    function connect() {
      // Don't reconnect with an expired token — log out instead
      if (jwtExpired(token)) {
        logout();
        return;
      }

      // Desktop app: use REACT_APP_SERVER_URL; browser: fall back to current origin.
      const base = process.env.REACT_APP_SERVER_URL
        || `${window.location.protocol}//${window.location.host}`;
      const wsUrl = base.replace(/^http/, 'ws') + `/ws/admin?token=${token}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        backoffRef.current = 3000; // reset backoff on successful connect
        const pending = bufRef.current.splice(0);
        pending.forEach(m => ws.send(typeof m === 'string' ? m : JSON.stringify(m)));
      };

      ws.onmessage = (e) => {
        try { cbRef.current(JSON.parse(e.data)); }
        catch {}
      };

      // Keep-alive ping every 20s
      const ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 20000);

      ws.onclose = () => {
        clearInterval(ping);
        const delay = backoffRef.current;
        backoffRef.current = Math.min(delay * 2, 30000); // exponential backoff, cap 30s
        timerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      clearTimeout(timerRef.current);
      wsRef.current?.close();
    };
  }, [token]);

  return { send };
}
